import { useState, useMemo } from "react";
import backendApi from "../api/backendApi";
import AlertModal from "../components/modal/AlertModal";
import { FiEye, FiEyeOff, FiCheckCircle, FiXCircle } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";

const ResetPasswordPage = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [title, setTitle] = useState("");

  const { state } = useLocation();
  const resetToken = state?.resetToken;
  const navigate = useNavigate();

  // Password policy checks
  const checks = useMemo(() => {
    const pwd = newPassword || "";
    return {
      length: pwd.length >= 8,
      lower: /[a-z]/.test(pwd),
      upper: /[A-Z]/.test(pwd),
      number: /[0-9]/.test(pwd),
      symbol: /[^A-Za-z0-9]/.test(pwd),
    };
  }, [newPassword]);

  const allValid = Object.values(checks).every(Boolean);
  const passwordsMatch =
    confirmPassword.length > 0 && newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!allValid) {
      setTitle("Password doesn’t meet requirements");
      setModalMessage("Please satisfy all password rules before submitting.");
      setIsSuccess(false);
      setModalOpen(true);
      return;
    }

    if (!passwordsMatch) {
      setTitle("Passwords do not match");
      setModalMessage("Re-enter your password.");
      setIsSuccess(false);
      setModalOpen(true);
      return;
    }

    try {
      await backendApi.post("/auth/reset-password", {
        newPassword,
        resetToken,
      });
      setTitle("Password successfully reset");
      setModalMessage("You may use your new password to log in later.");
      setIsSuccess(true);
      setModalOpen(true);
    } catch (err) {
      setTitle("Failed to reset password.");
      setModalMessage(
        "Please try again later. If the issue persists, please contact support."
      );
      setIsSuccess(false);
      setModalOpen(true);
    }
  };

  const Rule = ({ ok, label }) => (
    <div className="flex items-center gap-2 text-sm">
      {ok ? (
        <FiCheckCircle className="shrink-0 text-green-600" aria-hidden />
      ) : (
        <FiXCircle className="shrink-0 text-red-600" aria-hidden />
      )}
      <span className={ok ? "text-green-700" : "text-red-700"}>{label}</span>
    </div>
  );

  return (
    <div className="flex flex-col h-full justify-center items-center">
      <div className="md:max-w-lg w-[calc(100vw-2rem)] p-6 bg-white shadow rounded-3xl">
        <h2 className="text-2xl font-bold mb-4">Reset Your Password</h2>

        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {/* New Password */}
          <div>
            <label className="block text-sm font-medium mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                type={showNew ? "text" : "password"}
                placeholder="Enter new password"
                className="input w-full pr-10"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                aria-describedby="password-policy"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2"
                onClick={() => setShowNew((s) => !s)}
                aria-label={showNew ? "Hide password" : "Show password"}
              >
                {showNew ? <FiEye /> : <FiEyeOff />}
              </button>
            </div>

            {/* Live policy checklist */}
            <div
              id="password-policy"
              className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2"
            >
              <Rule ok={checks.lower} label="At least one lowercase letter" />
              <Rule ok={checks.upper} label="At least one uppercase letter" />
              <Rule ok={checks.number} label="At least one number" />
              <Rule ok={checks.symbol} label="At least one symbol" />
              <Rule ok={checks.length} label="Minimum 8 characters" />
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="Re-enter new password"
                className={`input w-full pr-10 ${
                  confirmPassword && !passwordsMatch ? "border-red-500" : ""
                }`}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2"
                onClick={() => setShowConfirm((s) => !s)}
                aria-label={showConfirm ? "Hide password" : "Show password"}
              >
                {showConfirm ? <FiEye /> : <FiEyeOff />}
              </button>
            </div>
            {confirmPassword && !passwordsMatch && (
              <p className="mt-2 text-sm text-red-600">
                Passwords do not match.
              </p>
            )}
          </div>

          <button
            type="submit"
            className={`bg-black text-white px-4 py-2 rounded w-full transition ${
              allValid && passwordsMatch
                ? "opacity-100 cursor-pointer"
                : "opacity-60 cursor-not-allowed"
            }`}
            disabled={!allValid || !passwordsMatch}
            aria-disabled={!allValid || !passwordsMatch}
          >
            Update Password
          </button>
        </form>

        <AlertModal
          isOpen={modalOpen}
          title={title}
          message={modalMessage}
          isOneButton={true}
          isSuccess={isSuccess}
          onSuccessOneButton={() => {
            navigate("/");
          }}
          onClose={() => {
            setModalOpen(false);
            window.location.reload();
          }}
        />
      </div>
    </div>
  );
};

export default ResetPasswordPage;
